import type { IdPUser } from "../idp-auth";
import { TrackingParams } from "./types";

export const EventType = {
  authentication: "authentication",
};

export const Page = {
  identifier: "identifier",
  password: "password",
  callback: "callback",
};

export const Section = {
  password: "password",
  identifier: "identifier",
  callback: "callback",
};

export const UnifiedAuthEvents = {
  FailedLoginAttemptBasic: (username?: string) => ({
    event: "failed_login_attempt",
    properties: {
      username: username,
      page: Page.password,
      section: Section.password,
      eventType: EventType.authentication,
      authType: "password",
      description: "Invalid username or password",
    },
  }),
  BasicAuthLoginComplete: (username?: string) => ({
    event: "basic_login_complete",
    properties: {
      username: username,
      page: Page.password,
      section: Section.password,
      eventType: EventType.authentication,
      description: "The login is complete for a basic auth user",
    },
  }),
  Auth0LoginStart: (email?: string) => ({
    event: "auth0_login_start",
    properties: {
      // Sent as username, the login identifier, not email: this event routes only to observability,
      // which drops email but keeps username so a login attempt can be tied back to an account.
      username: email,
      page: Page.identifier,
      section: Section.identifier,
      eventType: EventType.authentication,
      description: "The login is started for an auth0 user",
    },
  }),
  SendGridLoginComplete: (isUnifiedLogin: boolean, user?: IdPUser, username?: string) => ({
    event: "sendgrid_login_complete",
    properties: {
      firstName: user?.given_name,
      lastName: user?.family_name,
      email: user?.email,
      username: username,
      unifiedUserId: user?.["https://twilio.com/unified_user_id"],
      isUnifiedLogin: isUnifiedLogin,
      page: isUnifiedLogin ? Page.identifier : Page.password,
      section: isUnifiedLogin ? Section.identifier : Section.password,
      eventType: EventType.authentication,
      description: "The login is complete for a SendGrid user",
    },
  }),
  SendGridSignupComplete: (isUnifiedSignup: boolean, params?: TrackingParams, user?: IdPUser) => ({
    event: "sendgrid_signup_complete",
    properties: {
      firstName: user?.given_name,
      lastName: user?.family_name,
      email: user?.email,
      unifiedUserId: user?.["https://twilio.com/unified_user_id"],
      isUnifiedSignup: isUnifiedSignup,
      page: Page.callback,
      section: Section.callback,
      eventType: EventType.authentication,
      utm_source: params?.utm_source,
      utm_medium: params?.utm_medium,
      utm_campaign: params?.utm_campaign,
      utm_term: params?.utm_term,
      utm_content: params?.utm_content,
      utm_referrer: params?.utm_referrer,
      gclid: params?.gclid,
      fbclid: params?.fbclid,
      msclkid: params?.msclkid,
      li_fat_id: params?.li_fat_id,
      _gl: params?._gl,
      description: "The signup is complete for creating a new sendgrid user",
    },
  }),
  Auth0LoginComplete: (user?: IdPUser) => ({
    event: "auth0_login_complete",
    properties: {
      firstName: user?.given_name,
      lastName: user?.family_name,
      email: user?.email,
      unifiedUserId: user?.["https://twilio.com/unified_user_id"],
      page: Page.identifier,
      section: Section.identifier,
      eventType: EventType.authentication,
      description: "The login is complete for an auth0 user",
    },
  }),
  ResellerFallThrough: (
    errorId: string,
    outcome: "signed_in" | "retry" | "ipam_blocked" | "banned" | "maat",
    hasInviteToken: boolean
  ) => ({
    event: "reseller_fall_through",
    properties: {
      errorId,
      outcome,
      hasInviteToken,
      page: Page.callback,
      section: Section.callback,
      eventType: EventType.authentication,
      description:
        "Reseller teammate invite fall-through: sign-in attempted after accept rejection",
    },
  }),
};

export const TurnstileEvents = {
  Verified: (timeToVerifyMs: number, page?: string) => ({
    event: "turnstile_verified",
    properties: {
      time_to_verify_ms: Math.round(timeToVerifyMs),
      page,
      eventType: EventType.authentication,
      description: "Cloudflare Turnstile returned a verification token",
    },
  }),
  Error: (errorCode: string, page?: string) => {
    const parsed = Number(errorCode);
    const isNumeric = Number.isFinite(parsed);
    return {
      event: "turnstile_error",
      properties: {
        error_code: errorCode,
        error_code_numeric: isNumeric ? parsed : null,
        is_numeric_code: isNumeric,
        page,
        eventType: EventType.authentication,
        description: "Cloudflare Turnstile emitted an error callback",
      },
    };
  },
  Timeout: (timeoutMs: number, page?: string) => ({
    event: "turnstile_timeout",
    properties: {
      timeout_ms: timeoutMs,
      fallback_token: "TURNSTILE_UNAVAILABLE",
      page,
      eventType: EventType.authentication,
      description: "Cloudflare Turnstile did not respond within timeout; sent fallback token",
    },
  }),
};

/**
 * EVENT_ROUTING defines which analytics providers receive which events.
 * To add a new provider, add it to the relevant event arrays below.
 */
export const EVENT_ROUTING: Record<string, string[]> = {
  // Track events - explicit routing
  sendgrid_signup_complete: ["segment", "observability", "microsoft_ads"],
  sendgrid_login_complete: ["segment", "observability"],
  auth0_login_start: ["observability"],
  auth0_login_complete: ["observability"],
  basic_login_complete: ["observability"],
  failed_login_attempt: ["segment", "observability"],
  turnstile_verified: ["observability"],
  turnstile_error: ["observability"],
  turnstile_timeout: ["observability"],
  reseller_fall_through: ["observability"],

  // Defaults
  "track:*": ["segment", "observability"],
  identify: ["segment", "observability", "microsoft_ads"],
  page: ["segment"],
};
